import { useState, useEffect, useCallback, useRef } from 'react';
import { getOrders } from '../api/orders';

const DEFAULT_FILTERS = {
  status:             '',
  signal:             '',
  instrument:         '',
  broker_id:          '',
  strategy_id:        '',
  squareoff_trigger:  '',
  from_date:          '',
  to_date:            '',
};

export const useOrders = () => {
  const [orders,     setOrders]     = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [filters,    setFilters]    = useState(DEFAULT_FILTERS);
  const [pagination, setPagination] = useState({
    page:        1,
    limit:       20,
    total:       0,
    total_pages: 1,
    has_next:    false,
    has_prev:    false,
  });

  /* use a ref so fetchOrders always has latest values
     without causing infinite useEffect loops            */
  const filtersRef    = useRef(filters);
  const paginationRef = useRef(pagination);
  filtersRef.current    = filters;
  paginationRef.current = pagination;

  const fetchOrders = useCallback(async (overrideFilters, overridePage) => {
    setLoading(true);
    setError(null);
    try {
      const activeFilters = overrideFilters ?? filtersRef.current;
      const page          = overridePage   ?? paginationRef.current.page;

      /* strip empty/null/undefined params before sending */
      const params = Object.fromEntries(
        Object.entries({
          ...activeFilters,
          page,
          limit: paginationRef.current.limit,
        }).filter(([, v]) => v !== '' && v !== null && v !== undefined)
      );

      const res = await getOrders(params);

      /* Response shape:
         {
           success: true,
           message: "Orders retrieved successfully",
           data: {
             orders: [...],
             pagination: { page, limit, total, total_pages, has_next, has_prev }
           }
         }
         axios wraps it: res.data = above object
         so res.data.data = { orders, pagination }               */
      const payload = res.data?.data ?? res.data;

      if (Array.isArray(payload)) {
        /* flat array fallback */
        setOrders(payload);
      } else {
        setOrders(payload?.orders ?? []);
        if (payload?.pagination) {
          setPagination(p => ({ ...p, ...payload.pagination }));
        }
      }
    } catch (e) {
      const msg = e?.response?.data?.message ?? e?.message ?? 'Failed to load orders';
      setError(msg);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, []); // stable — uses refs internally

  /* fetch on mount */
  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  /* apply one filter key/value — resets to page 1 */
  const applyFilter = useCallback((key, value) => {
    const next = { ...filtersRef.current, [key]: value };
    setFilters(next);
    setPagination(p => ({ ...p, page: 1 }));
    fetchOrders(next, 1);
  }, [fetchOrders]);

  /* reset all filters */
  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setPagination(p => ({ ...p, page: 1 }));
    fetchOrders(DEFAULT_FILTERS, 1);
  }, [fetchOrders]);

  /* go to specific page */
  const goToPage = useCallback((page) => {
    setPagination(p => ({ ...p, page }));
    fetchOrders(filtersRef.current, page);
  }, [fetchOrders]);

  const refresh = useCallback(() => {
    fetchOrders(filtersRef.current, paginationRef.current.page);
  }, [fetchOrders]);

  return {
    orders,
    loading,
    error,
    filters,
    pagination,
    applyFilter,
    resetFilters,
    goToPage,
    refresh,
  };
};